// https://github.com/synapticon/somanet_software/blob/32edf5ef572ea7d840aabe4a26e0f3d31f05caab/sw_applications/app_motion_drive/test/pytest/somanet_icmu_helpers.py

import { HardwareDescription } from './hardware-description';
import { findSomanetProductById, resolveEncoderProductId } from './product';
import { MotionMasterMessage } from './types';

/**
 * Mapping of key configuration register addresses for Circulo Integrated Encoders (e.g., iC-MU, iC-PVL).
 *
 * Each entry maps a configuration register's address in EEPROM to its corresponding
 * address in RAM. This mapping allows read/write access to configuration data via
 * either persistent storage (EEPROM) or volatile memory (RAM).
 */
export const circuloIntegratedEncoderKeyConfigurationRegisterAddresses: { eeprom: number; ram: number }[] = [
  { eeprom: 0x00, ram: 0x00 },
  { eeprom: 0x01, ram: 0x01 },
  { eeprom: 0x02, ram: 0x02 },
  { eeprom: 0x03, ram: 0x03 },
  { eeprom: 0x04, ram: 0x04 },
  { eeprom: 0x05, ram: 0x05 },
  { eeprom: 0x06, ram: 0x06 },
  { eeprom: 0x07, ram: 0x07 },
  { eeprom: 0x08, ram: 0x08 },
  { eeprom: 0x09, ram: 0x09 },
  { eeprom: 0x0a, ram: 0x0a },
  { eeprom: 0x0b, ram: 0x0b },
  { eeprom: 0x0c, ram: 0x0c },
  { eeprom: 0x0d, ram: 0x0d },
  { eeprom: 0x0e, ram: 0x0e },
  { eeprom: 0x0f, ram: 0x0f },
  { eeprom: 0x10, ram: 0x10 },
  { eeprom: 0x11, ram: 0x11 },
  { eeprom: 0x12, ram: 0x12 },
  { eeprom: 0x13, ram: 0x13 },
  { eeprom: 0x14, ram: 0x14 },
  { eeprom: 0x15, ram: 0x15 },
  { eeprom: 0x16, ram: 0x16 },
  { eeprom: 0x17, ram: 0x17 },
  { eeprom: 0x18, ram: 0x18 },
  { eeprom: 0x19, ram: 0x52 },
  { eeprom: 0x1a, ram: 0x53 },
  { eeprom: 0x1b, ram: 0x54 },
  { eeprom: 0x1c, ram: 0x55 },
  { eeprom: 0x1d, ram: 0x56 },
  { eeprom: 0x1e, ram: 0x57 },
  { eeprom: 0x1f, ram: 0x58 },
  { eeprom: 0x20, ram: 0x59 },
];

/**
 * I²C device IDs (addresses) used to access specific memory areas and registers
 * of Circulo Integrated Encoders such as iC-MU and iC-PVL.
 *
 * These values are used as the 7-bit device address (plus R/W bit) in I²C transactions,
 * defining whether the access targets EEPROM or RAM, and whether full or limited access is allowed.
 *
 * EEPROM access allows for reading and writing of persistent configuration data,
 * while iC-PVL access allows interaction with runtime registers. Limited access
 * typically restricts operations to a subset of available registers.
 */
export enum CirculoIntegratedEncoderI2CDeviceId {
  /** I²C address for writing to EEPROM memory. */
  WRITE_EEPROM = 0xa0,

  /** I²C address for reading from EEPROM memory. */
  READ_EEPROM = 0xa1,

  /** I²C address for limited write access to iC-PVL registers. */
  WRITE_ICPVL_LIMITED = 0xc0,

  /** I²C address for limited read access to iC-PVL registers. */
  READ_ICPVL_LIMITED = 0xc1,

  /** I²C address for full write access to iC-PVL registers. */
  WRITE_ICPVL = 0xc2,

  /** I²C address for full read access to iC-PVL registers. */
  READ_ICPVL = 0xc3,
}

/**
 * A key-value object representing the `CirculoIntegratedEncoderI2CDeviceId` enum,
 * where each key is the string name of an I²C device ID, and each value is its
 * corresponding numeric address.
 *
 * This object is generated by filtering out the reverse mappings from the enum
 * (numeric keys), leaving only the name-to-value entries for use in things like
 * dropdowns or selection lists.
 *
 * @example
 * {
 *   READ_EEPROM: 161,
 *   READ_ICPVL: 195
 *   READ_ICPVL_LIMITED: 193,
 *   WRITE_EEPROM: 160,
 *   WRITE_ICPVL: 194,
 *   WRITE_ICPVL_LIMITED: 192,
 * }
 */
export const circuloIntegratedEncoderI2CDeviceIdOptions = Object.entries(CirculoIntegratedEncoderI2CDeviceId)
  .filter(([key]) => isNaN(Number(key))) // exclude numeric keys (value-to-name)
  .reduce((acc, [key, value]) => {
    acc[key] = value as number;
    return acc;
  }, {} as Record<string, number>);

export class CirculoIntegratedEncoderIcmuParameters {
  static GC_M = { address: 0x00, lsb: 6, msb: 7 };
  static GF_M = { address: 0x00, lsb: 0, msb: 5 };
  static GX_M = { address: 0x01, lsb: 0, msb: 6 };
  static VOSS_M = { address: 0x02, lsb: 0, msb: 6 };
  static VOSC_M = { address: 0x03, lsb: 0, msb: 6 };
  static PH_M = { address: 0x04, lsb: 0, msb: 6 };
  static ENAC = { address: 0x05, lsb: 7, msb: 7 };
  static CIBM = { address: 0x05, lsb: 0, msb: 3 };
  static GC_N = { address: 0x06, lsb: 6, msb: 7 };
  static GF_N = { address: 0x06, lsb: 0, msb: 5 };
  static GX_N = { address: 0x07, lsb: 0, msb: 6 };
  static VOSS_N = { address: 0x08, lsb: 0, msb: 6 };
  static VOSC_N = { address: 0x09, lsb: 0, msb: 6 };
  static PH_N = { address: 0x0a, lsb: 0, msb: 6 };
  static MODEB = { address: 0x0b, lsb: 4, msb: 6 };
  static MODEA = { address: 0x0b, lsb: 0, msb: 2 };
  static CFGEW = { address: 0x0c, lsb: 0, msb: 7 };
  static ACC_STAT = { address: 0x0d, lsb: 7, msb: 7 };
  static NCHK_CRC = { address: 0x0d, lsb: 6, msb: 6 };
  static NCHK_NON = { address: 0x0d, lsb: 5, msb: 5 };
  static ACRM_RES = { address: 0x0d, lsb: 4, msb: 4 };
  static EMTD = { address: 0x0d, lsb: 0, msb: 2 };
  static ESSI_MT = { address: 0x0e, lsb: 6, msb: 7 };
  static ROT_MT = { address: 0x0e, lsb: 5, msb: 5 };
  static LIN = { address: 0x0e, lsb: 4, msb: 4 };
  static FILT = { address: 0x0e, lsb: 0, msb: 2 };
  static SPO_MT = { address: 0x0f, lsb: 4, msb: 7 };
  static MPC = { address: 0x0f, lsb: 0, msb: 3 };
  static GET_MT = { address: 0x10, lsb: 7, msb: 7 };
  static CHK_MT = { address: 0x10, lsb: 6, msb: 6 };
  static SBL_MT = { address: 0x10, lsb: 4, msb: 5 };
  static MODE_MT = { address: 0x10, lsb: 0, msb: 3 };
  static OUT_ZERO = { address: 0x11, lsb: 5, msb: 7 };
  static OUT_MSB = { address: 0x11, lsb: 0, msb: 4 };
  static GSSI = { address: 0x12, lsb: 7, msb: 7 };
  static RSSI = { address: 0x12, lsb: 6, msb: 6 };
  static MODE_ST = { address: 0x12, lsb: 4, msb: 5 };
  static OUT_LSB = { address: 0x12, lsb: 0, msb: 3 };
  static RESABZ = { address: 0x13, lsb: 0, msb: 15 };
  static ROT = { address: 0x015, lsb: 7, msb: 7 };
  static SS_AB = { address: 0x15, lsb: 4, msb: 5 };
  static ENIF_AUTO = { address: 0x15, lsb: 3, msb: 3 };
  static FRQAB = { address: 0x15, lsb: 0, msb: 2 };
  static LENZ = { address: 0x16, lsb: 6, msb: 7 };
  static CHYS_AB = { address: 0x16, lsb: 4, msb: 5 };
  static PP60UVW = { address: 0x16, lsb: 3, msb: 3 };
  static INV_A = { address: 0x16, lsb: 2, msb: 2 };
  static INV_B = { address: 0x16, lsb: 1, msb: 1 };
  static INV_Z = { address: 0x16, lsb: 0, msb: 0 };
  static RPL = { address: 0x17, lsb: 6, msb: 7 };
  static PPUVW = { address: 0x17, lsb: 0, msb: 5 };
  static TEST = { address: 0x18, lsb: 0, msb: 7 };
  static OFF_ABZ_1 = { address: 0x1e, lsb: 4, msb: 15 };
  static OFF_POS = { address: 0x20, lsb: 0, msb: 23 };
  static OFF_COM = { address: 0x23, lsb: 4, msb: 15 };
  static PA0_CONF = { address: 0x25, lsb: 0, msb: 7 };
  static ACGAIN_M = { address: 0x2b, lsb: 3, msb: 4 };
  static AFGAIN_M = { address: 0x2b, lsb: 0, msb: 2 };
  static ACGAIN_N = { address: 0x2f, lsb: 3, msb: 4 };
  static AFGAIN_N = { address: 0x2f, lsb: 0, msb: 2 };
  static BANKSEL = { address: 0x40, lsb: 0, msb: 4 };
  static EDSBANK = { address: 0x41, lsb: 0, msb: 7 };
  static PROFILE_ID = { address: 0x42, lsb: 0, msb: 15 };
  static SERIAL = { address: 0x44, lsb: 0, msb: 31 };
  static OFF_ABZ_2 = { address: 0x48, lsb: 0, msb: 23 };
  static OFF_UVW = { address: 0x4b, lsb: 4, msb: 15 };
  static PRES_POS = { address: 0x4d, lsb: 4, msb: 39 };
  static SPO_0 = { address: 0x52, lsb: 4, msb: 7 };
  static SPO_BASE = { address: 0x52, lsb: 0, msb: 3 };
  static SPO_2 = { address: 0x53, lsb: 4, msb: 7 };
  static SPO_1 = { address: 0x53, lsb: 0, msb: 3 };
  static SPO_4 = { address: 0x54, lsb: 4, msb: 7 };
  static SPO_3 = { address: 0x54, lsb: 0, msb: 3 };
  static SPO_6 = { address: 0x55, lsb: 4, msb: 7 };
  static SPO_5 = { address: 0x55, lsb: 0, msb: 3 };
  static SPO_8 = { address: 0x56, lsb: 4, msb: 7 };
  static SPO_7 = { address: 0x56, lsb: 0, msb: 3 };
  static SPO_10 = { address: 0x57, lsb: 4, msb: 7 };
  static SPO_9 = { address: 0x57, lsb: 0, msb: 3 };
  static SPO_12 = { address: 0x58, lsb: 4, msb: 7 };
  static SPO_11 = { address: 0x58, lsb: 0, msb: 3 };
  static SPO_14 = { address: 0x59, lsb: 4, msb: 7 };
  static SPO_13 = { address: 0x59, lsb: 0, msb: 3 };
  static RPL_RESET = { address: 0x5a, lsb: 0, msb: 7 };
  static I2C_DEV_START = { address: 0x5b, lsb: 0, msb: 7 };
  static I2C_RAM_START = { address: 0x5c, lsb: 0, msb: 7 };
  static I2C_RAM_END = { address: 0x5d, lsb: 0, msb: 7 };
  static I2C_DEVID = { address: 0x5e, lsb: 0, msb: 7 };
  static I2C_RETRY = { address: 0x5f, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_1 = { address: 0x60, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_2 = { address: 0x61, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_3 = { address: 0x62, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_4 = { address: 0x63, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_5 = { address: 0x64, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_6 = { address: 0x65, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_7 = { address: 0x66, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_8 = { address: 0x67, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_9 = { address: 0x68, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_10 = { address: 0x69, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_11 = { address: 0x6a, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_12 = { address: 0x6b, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_13 = { address: 0x6c, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_14 = { address: 0x6d, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_15 = { address: 0x6e, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_16 = { address: 0x6f, lsb: 0, msb: 7 };
  static EVENT_COUNT = { address: 0x73, lsb: 0, msb: 7 };
  static HARD_REV = { address: 0x74, lsb: 0, msb: 7 };
  static CMD_MU = { address: 0x75, lsb: 0, msb: 7 };
  static STATUS0 = { address: 0x76, lsb: 0, msb: 7 };
  static STATUS1 = { address: 0x77, lsb: 0, msb: 7 };
  static DEV_ID = { address: 0x78, lsb: 0, msb: 47 };
  static MFG_ID = { address: 0x7e, lsb: 0, msb: 15 };
}

export class CirculoIntegratedEncoderIcpvlParameters {
  static EN_PAR = { address: 0x00, lsb: 6, msb: 7 };
  static EN_ERR = { address: 0x00, lsb: 4, msb: 5 };
  static DIR = { address: 0x00, lsb: 3, msb: 3 };
  static ST_GRAY = { address: 0x00, lsb: 2, msb: 2 };
  static MT_GRAY = { address: 0x00, lsb: 1, msb: 1 };
  static INT_MODE = { address: 0x00, lsb: 0, msb: 0 };
  static OS = { address: 0x01, lsb: 5, msb: 7 };
  static MT_BW = { address: 0x01, lsb: 0, msb: 4 };
  static PCR = { address: 0x02, lsb: 0, msb: 7 };
  static EN_WRN = { address: 0x03, lsb: 7, msb: 7 };
  static BAT_MON = { address: 0x03, lsb: 6, msb: 6 };
  static A_MAX = { address: 0x03, lsb: 3, msb: 5 };
  static IBIAS = { address: 0x03, lsb: 0, msb: 2 };
  static FIXED_ZERO_1 = { address: 0x04, lsb: 7, msb: 7 };
  static NOMAG = { address: 0x04, lsb: 5, msb: 6 };
  static ATHR = { address: 0x04, lsb: 3, msb: 4 };
  static ONAX = { address: 0x04, lsb: 2, msb: 2 };
  static POLEWID = { address: 0x04, lsb: 0, msb: 1 };
  static I2C_POS = { address: 0x05, lsb: 7, msb: 7 };
  static PCR_OUT = { address: 0x05, lsb: 6, msb: 6 };
  static SYNC_BW = { address: 0x05, lsb: 4, msb: 5 };
  static BAT_THR = { address: 0x05, lsb: 2, msb: 3 };
  static HYS = { address: 0x05, lsb: 1, msb: 1 };
  static ABQUAD = { address: 0x05, lsb: 0, msb: 0 };
  static CRC_CFG = { address: 0x06, lsb: 0, msb: 7 };
  static MT_COUNT = { address: 0x07, lsb: 0, msb: 39 };
  static CRC_CTR = { address: 0x0c, lsb: 0, msb: 7 };
  static FIXED_ZERO_2 = { address: 0x0d, lsb: 3, msb: 7 };
  static SYNC = { address: 0x0d, lsb: 0, msb: 2 };
  static CHIP_REL = { address: 0x0f, lsb: 0, msb: 7 };
  static PRESET = { address: 0x10, lsb: 7, msb: 7 };
  static PDR = { address: 0x10, lsb: 6, msb: 6 };
  static BAT_WRN = { address: 0x10, lsb: 5, msb: 5 };
  static BAT_ERR = { address: 0x10, lsb: 4, msb: 4 };
  static POS_ERR = { address: 0x10, lsb: 3, msb: 3 };
  static CTR_ERR = { address: 0x10, lsb: 2, msb: 2 };
  static CFG_ERR = { address: 0x10, lsb: 1, msb: 1 };
  static STUP_ERR = { address: 0x10, lsb: 0, msb: 0 };
  static CMD = { address: 0x11, lsb: 0, msb: 7 };
  static SLEEP_ST = { address: 0x12, lsb: 7, msb: 7 };
  static NOMAG_ST = { address: 0x12, lsb: 6, msb: 6 };
  static ACTIVE_ST = { address: 0x12, lsb: 5, msb: 5 };
  static POWON_ST = { address: 0x12, lsb: 4, msb: 4 };
  static NOMAG_L = { address: 0x12, lsb: 2, msb: 2 };
  static MAG_ERR = { address: 0x12, lsb: 1, msb: 1 };
  static AMPL_ERR = { address: 0x12, lsb: 0, msb: 0 };
}

export class CirculoIntegratedEncoderEepromParameters {
  // iC-MU registers
  static GC_M = { address: 0x00, lsb: 6, msb: 7 };
  static GF_M = { address: 0x00, lsb: 0, msb: 5 };
  static GX_M = { address: 0x01, lsb: 0, msb: 6 };
  static VOSS_M = { address: 0x02, lsb: 0, msb: 6 };
  static VOSC_M = { address: 0x03, lsb: 0, msb: 6 };
  static PH_M = { address: 0x04, lsb: 0, msb: 6 };
  static ENAC = { address: 0x05, lsb: 7, msb: 7 };
  static CIBM = { address: 0x05, lsb: 0, msb: 3 };
  static GC_N = { address: 0x06, lsb: 6, msb: 7 };
  static GF_N = { address: 0x06, lsb: 0, msb: 5 };
  static GX_N = { address: 0x07, lsb: 0, msb: 6 };
  static VOSS_N = { address: 0x08, lsb: 0, msb: 6 };
  static VOSC_N = { address: 0x09, lsb: 0, msb: 6 };
  static PH_N = { address: 0x0a, lsb: 0, msb: 6 };
  static MODEB = { address: 0x0b, lsb: 4, msb: 6 };
  static MODEA = { address: 0x0b, lsb: 0, msb: 2 };
  static CFGEW = { address: 0x0c, lsb: 0, msb: 7 };
  static ACC_STAT = { address: 0x0d, lsb: 7, msb: 7 };
  static NCHK_CRC = { address: 0x0d, lsb: 6, msb: 6 };
  static NCHK_NON = { address: 0x0d, lsb: 5, msb: 5 };
  static ACRM_RES = { address: 0x0d, lsb: 4, msb: 4 };
  static EMTD = { address: 0x0d, lsb: 0, msb: 2 };
  static ESSI_MT = { address: 0x0e, lsb: 6, msb: 7 };
  static ROT_MT = { address: 0x0e, lsb: 5, msb: 5 };
  static LIN = { address: 0x0e, lsb: 4, msb: 4 };
  static FILT = { address: 0x0e, lsb: 0, msb: 2 };
  static SPO_MT = { address: 0x0f, lsb: 4, msb: 7 };
  static MPC = { address: 0x0f, lsb: 0, msb: 3 };
  static GET_MT = { address: 0x10, lsb: 7, msb: 7 };
  static CHK_MT = { address: 0x10, lsb: 6, msb: 6 };
  static SBL_MT = { address: 0x10, lsb: 4, msb: 5 };
  static MODE_MT = { address: 0x10, lsb: 0, msb: 3 };
  static OUT_ZERO = { address: 0x11, lsb: 5, msb: 7 };
  static OUT_MSB = { address: 0x11, lsb: 0, msb: 4 };
  static GSSI = { address: 0x12, lsb: 7, msb: 7 };
  static RSSI = { address: 0x12, lsb: 6, msb: 6 };
  static MODE_ST = { address: 0x12, lsb: 4, msb: 5 };
  static OUT_LSB = { address: 0x12, lsb: 0, msb: 3 };
  static RESABZ = { address: 0x13, lsb: 0, msb: 15 };
  static ROT = { address: 0x015, lsb: 7, msb: 7 };
  static SS_AB = { address: 0x15, lsb: 4, msb: 5 };
  static ENIF_AUTO = { address: 0x15, lsb: 3, msb: 3 };
  static FRQAB = { address: 0x15, lsb: 0, msb: 2 };
  static LENZ = { address: 0x16, lsb: 6, msb: 7 };
  static CHYS_AB = { address: 0x16, lsb: 4, msb: 5 };
  static PP60UVW = { address: 0x16, lsb: 3, msb: 3 };
  static INV_A = { address: 0x16, lsb: 2, msb: 2 };
  static INV_B = { address: 0x16, lsb: 1, msb: 1 };
  static INV_Z = { address: 0x16, lsb: 0, msb: 0 };
  static RPL = { address: 0x17, lsb: 6, msb: 7 };
  static PPUVW = { address: 0x17, lsb: 0, msb: 5 };
  static TEST = { address: 0x18, lsb: 0, msb: 7 };
  static SPO_0 = { address: 0x19, lsb: 4, msb: 7 };
  static SPO_BASE = { address: 0x19, lsb: 0, msb: 3 };
  static SPO_2 = { address: 0x1a, lsb: 4, msb: 7 };
  static SPO_1 = { address: 0x1a, lsb: 0, msb: 3 };
  static SPO_4 = { address: 0x1b, lsb: 4, msb: 7 };
  static SPO_3 = { address: 0x1b, lsb: 0, msb: 3 };
  static SPO_6 = { address: 0x1c, lsb: 4, msb: 7 };
  static SPO_5 = { address: 0x1c, lsb: 0, msb: 3 };
  static SPO_8 = { address: 0x1d, lsb: 4, msb: 7 };
  static SPO_7 = { address: 0x1d, lsb: 0, msb: 3 };
  static SPO_10 = { address: 0x1e, lsb: 4, msb: 7 };
  static SPO_9 = { address: 0x1e, lsb: 0, msb: 3 };
  static SPO_12 = { address: 0x1f, lsb: 4, msb: 7 };
  static SPO_11 = { address: 0x1f, lsb: 0, msb: 3 };
  static SPO_14 = { address: 0x20, lsb: 4, msb: 7 };
  static SPO_13 = { address: 0x20, lsb: 0, msb: 3 };
  static CRC16 = { address: 0x21, lsb: 0, msb: 15 };
  static OFF_ABZ = { address: 0x23, lsb: 4, msb: 39 };
  static OFF_UVW = { address: 0x28, lsb: 4, msb: 15 };
  static PRES_POS = { address: 0x2a, lsb: 4, msb: 39 };
  static CRC8 = { address: 0x2f, lsb: 0, msb: 7 };
  static PA0_CONF = { address: 0x30, lsb: 0, msb: 7 };
  static EDSBANK = { address: 0x31, lsb: 0, msb: 7 };
  static PROFILE_ID = { address: 0x32, lsb: 0, msb: 15 };
  static SERIAL = { address: 0x34, lsb: 0, msb: 31 };
  static DEV_ID = { address: 0x38, lsb: 0, msb: 47 };
  static MFG_ID = { address: 0x3e, lsb: 0, msb: 15 };
  // iC-PVL registers
  static EN_PAR = { address: 0x40, lsb: 6, msb: 7 };
  static EN_ERR = { address: 0x40, lsb: 4, msb: 5 };
  static DIR = { address: 0x40, lsb: 3, msb: 3 };
  static ST_GRAY = { address: 0x40, lsb: 2, msb: 2 };
  static MT_GRAY = { address: 0x40, lsb: 1, msb: 1 };
  static INT_MODE = { address: 0x40, lsb: 0, msb: 0 };
  static OS = { address: 0x41, lsb: 5, msb: 7 };
  static MT_BW = { address: 0x41, lsb: 0, msb: 4 };
  static PCR = { address: 0x42, lsb: 0, msb: 7 };
  static EN_WRN = { address: 0x43, lsb: 7, msb: 7 };
  static BAT_MON = { address: 0x43, lsb: 6, msb: 6 };
  static A_MAX = { address: 0x43, lsb: 3, msb: 5 };
  static IBIAS = { address: 0x43, lsb: 0, msb: 2 };
  static FIXED_ZERO_1 = { address: 0x44, lsb: 7, msb: 7 };
  static NOMAG = { address: 0x44, lsb: 5, msb: 6 };
  static ATHR = { address: 0x44, lsb: 3, msb: 4 };
  static ONAX = { address: 0x44, lsb: 2, msb: 2 };
  static POLEWID = { address: 0x44, lsb: 0, msb: 1 };
  static I2C_POS = { address: 0x45, lsb: 7, msb: 7 };
  static PCR_OUT = { address: 0x45, lsb: 6, msb: 6 };
  static SYNC_BW = { address: 0x45, lsb: 4, msb: 5 };
  static BAT_THR = { address: 0x45, lsb: 2, msb: 3 };
  static HYS = { address: 0x45, lsb: 1, msb: 1 };
  static ABQUAD = { address: 0x45, lsb: 0, msb: 0 };
  static CRC_CFG = { address: 0x46, lsb: 0, msb: 7 };
  static MT_PREL = { address: 0x47, lsb: 0, msb: 39 };
  static CRC_CTR = { address: 0x4c, lsb: 0, msb: 7 };
}

/**
 * Commands for the iC-MU encoder's `CMD_MU` register (address 0x75).
 *
 * These command codes control various internal operations of the iC-MU encoder,
 * such as memory writes, resets, communication settings, and maintenance counters.
 */
export class CirculoIntegratedEncoderIcmuCommands {
  /** Write all configuration settings to EEPROM. */
  static WRITE_ALL = 0x01;

  /** Disable EEPROM write access. */
  static WRITE_OFF = 0x02;

  /** Reset absolute position data. */
  static ABS_RESET = 0x03;

  /** Start non-volatile memory verification. */
  static NON_VER = 0x04;

  /** Reset multiturn counter. */
  static MT_RESET = 0x05;

  /** Verify multiturn counter value. */
  static MT_VER = 0x06;

  /** Perform a software reset of the device. */
  static SOFT_RESET = 0x07;

  /** Perform a software presence check. */
  static SOFT_PRES = 0x08;

  /** Perform a software + EEPROM presence check. */
  static SOFT_E2P_PRES = 0x09;

  /** Set or test I²C communication path. */
  static I2C_COM = 0x0a;

  /** Trigger an event counter update. */
  static EVENT_COUNT = 0x0b;

  /** Toggle or test switch functionality. */
  static SWITCH = 0x0c;

  /** Verify stored CRC against calculated CRC. */
  static CRC_VER = 0x0d;

  /** Trigger CRC calculation. */
  static CRC_CALC = 0x0e;

  /** Set multiturn counter (manual value assignment). */
  static SET_MTC = 0x0f;

  /** Reset multiturn counter to zero. */
  static RES_MTC = 0x10;
}

/**
 * Commands for the iC-PVL encoder's `CMD` register (address 0x11).
 *
 * These command values are written to the `CMD` register to control
 * the behavior of the encoder, including reset, sleep mode, and clearing status.
 */
export class CirculoIntegratedEncoderIcpvlCommands {
  /** Perform a software reset of the device. */
  static RESET = 0x02;

  /** Reboot the device, similar to power cycling. */
  static REBOOT = 0x03;

  /** Enable sleep mode to reduce power consumption. */
  static SLEEP_ENABLE = 0x04;

  /** Clear internal status flags or errors (software clear). */
  static SCLR = 0x05;

  /** Disable sleep mode and resume normal operation. */
  static SLEEP_DISABLE = 0x07;
}

/**
 * Returns a color representing the encoder calibration quality for Circulo devices.
 *
 * The color is determined by the maximum-value percentage:
 *
 * - `50–100%`: **Green** (`#6ba853`) — robust calibration
 * - `20–50%`: **Yellow** (`#aeb303`) — acceptable, small displacements tolerated
 * - `0–20%`: **Orange** (`#e69138`) — weak, avoid mechanical displacement
 * - `≤ 0%`: **Red** (`#fb262e`) — invalid calibration, phase error too high
 *
 * @param percentage - The maximum-value percentage obtained from encoder calibration.
 * @returns A hex color string representing the calibration quality, or an empty string if out of range.
 */
export function getCirculoEncoderCalibrationMaxValuePercentageColor(percentage: number) {
  if (percentage >= 50 && percentage <= 100) {
    return '#6ba853';
  } else if (percentage > 20 && percentage < 50) {
    return '#aeb303';
  } else if (percentage > 0 && percentage <= 20) {
    return '#e69138';
  } else if (percentage <= 0) {
    return '#fb262e';
  }
  return '';
}

/**
 * Returns a descriptive label explaining the quality of the encoder calibration
 * based on the maximum-value percentage for Circulo devices.
 *
 * The label communicates how robust the calibration is with respect to mechanical
 * displacement and thermal expansion:
 *
 * - `50–100%`: Robust against displacement and temperature changes
 * - `20–50%`: Robust to small mechanical or thermal variations
 * - `0–20%`: Calibration succeeded but sensitive; avoid displacement
 * - `≤ 0%`: Calibration failed; phase error too high
 *
 * @param percentage - The maximum-value percentage obtained from encoder calibration.
 * @returns A human-readable label describing calibration quality, or an empty string if out of range.
 */
export function getCirculoEncoderCalibrationMaxValuePercentageLabel(percentage: number) {
  if (percentage >= 50 && percentage <= 100) {
    return 'Calibration was successful. Encoder system is robust to mechanical displacements and temperature expansions.';
  } else if (percentage > 20 && percentage < 50) {
    return 'Calibration was successful. Encoder system is robust to small mechanical displacements and temperature expansions.';
  } else if (percentage > 0 && percentage <= 20) {
    return 'Calibration was successful. Mechanical displacement and temperature expansions should be avoided.';
  } else if (percentage <= 0) {
    return "Phase error is too high. The absolute position can't be computed reliably. Adjust mechanical position and repeat calibration.";
  }
  return '';
}

/**
 * External Circulo encoder types accepted by the Circulo encoder narrow angle calibration procedure.
 *
 * The values mirror the enum the Motion Master proto file keeps commented out. They name the
 * magnetic ring of a Circulo mounted on another drive; an integrated ring is left at
 * {@link CirculoExternalEncoderType.NONE}.
 */
export enum CirculoExternalEncoderType {
  NONE = 0,
  C7_INNER = 1,
  C7_OUTER = 2,
  C9_INNER = 3,
  C9_OUTER = 4,
}

/** Max permissible phase error of a Circulo 7 magnetic ring, in encoder increments. */
export const circulo7MaxPermissiblePhaseError = 256;

/** Max permissible phase error of a Circulo 9 magnetic ring, in encoder increments. */
export const circulo9MaxPermissiblePhaseError = 128;

/**
 * Returns the max permissible phase error of the ring a calibration measures, derived from the product, in
 * encoder increments.
 *
 * Motion Master 5.8.3 and later report the limit of the measured ring in every calibration progress status.
 * This function is the fallback for an older Motion Master, which reports none. It only knows two rings: a
 * Circulo 7 ring tolerates 256 increments and a Circulo 9 ring 128. Any other ring gets 128, which may not
 * be its limit.
 *
 * An external encoder type names the ring outright, so it wins over the product: that ring sits on a
 * drive of an unrelated size. Everything else is answered by the product group of the device itself,
 * which leaves a Circulo 9, a Circulo NG and an unrecognised product on 128.
 *
 * @param productId - The device carrying the encoder: a product id, a serial number or a `HardwareDescription`
 * @param externalEncoderType - The external ring the procedure was asked to calibrate, if any
 * @returns The max permissible phase error in encoder increments
 */
export function getCirculoEncoderMaxPermissiblePhaseError(
  productId?: number | string | HardwareDescription | null,
  externalEncoderType: CirculoExternalEncoderType | number = CirculoExternalEncoderType.NONE
): number {
  if (
    externalEncoderType === CirculoExternalEncoderType.C7_INNER ||
    externalEncoderType === CirculoExternalEncoderType.C7_OUTER
  ) {
    return circulo7MaxPermissiblePhaseError;
  }

  if (
    externalEncoderType === CirculoExternalEncoderType.C9_INNER ||
    externalEncoderType === CirculoExternalEncoderType.C9_OUTER
  ) {
    return circulo9MaxPermissiblePhaseError;
  }

  return findSomanetProductById(resolveEncoderProductId(productId))?.group === 'Circulo 7'
    ? circulo7MaxPermissiblePhaseError
    : circulo9MaxPermissiblePhaseError;
}

/**
 * Result of Circulo encoder calibration computation.
 */
export interface CirculoEncoderCalibrationResult {
  /** Paired data points: [position, phaseError] */
  data: number[][];
  /** Maximum-value percentage indicating calibration quality (0–100) */
  value: number;
  /** Human-readable meaning of the calibration percentage */
  meaning: string;
  /** Max permissible phase error the percentage was computed against, in encoder increments */
  maxPermissiblePhaseError: number;
}

/**
 * Computes the Circulo encoder calibration result from position and phaseError arrays.
 *
 * Handles empty or mismatched inputs gracefully by returning null.
 *
 * @param position - Array of encoder positions
 * @param phaseError - Array of corresponding phase errors
 * @param maxPermissiblePhaseError - Max permissible phase error of the measured ring, in encoder increments.
 *   The percentage is the measured phase error expressed against this limit, so the limit of another ring
 *   misreports the quality: a Circulo 9 ring scored against the Circulo 7 limit reads roughly twice the
 *   headroom it has. The limit comes back in the result.
 * @returns Calibration result or null if inputs are invalid or empty
 */
export function computeCirculoEncoderCalibrationResult(
  position: number[] | undefined,
  phaseError: number[] | undefined,
  maxPermissiblePhaseError: number
): CirculoEncoderCalibrationResult | null {
  // Return null if inputs are missing or lengths don't match
  if (!position || !phaseError || position.length !== phaseError.length || position.length === 0) {
    return null;
  }

  // Pair valid [position, phaseError] points
  const data: number[][] = position
    .map((p, i) => {
      const err = phaseError[i];
      return Number.isFinite(p) && Number.isFinite(err) ? [p, err] : undefined;
    })
    .filter((pt): pt is number[] => !!pt);

  if (data.length === 0) {
    return null;
  }

  // Find the data point with the largest absolute phase error
  const maxPoint = data.reduce((max, point) => (Math.abs(point[1]) > Math.abs(max[1]) ? point : max), data[0]);

  const maxValuePercentage = Math.max(100 - Math.floor((Math.abs(maxPoint[1]) / maxPermissiblePhaseError) * 100), 0);

  return {
    data,
    value: maxValuePercentage,
    meaning: getCirculoEncoderCalibrationMaxValuePercentageLabel(maxValuePercentage),
    maxPermissiblePhaseError,
  };
}

/**
 * Computes the Circulo encoder calibration result of one calibration iteration.
 *
 * The result is scored against the limit the progress status carries. Motion Master 5.8.3 and later report
 * it, and `startCirculoEncoderNarrowAngleCalibrationProcedure` fills it in for an older Motion Master. This
 * is the limit OBLAC Drives scores against, so both show the same percentage.
 *
 * @param progress - Progress of a Circulo encoder narrow angle calibration status
 * @returns Calibration result, or null if the progress carries no data or no limit
 */
export function computeCirculoEncoderCalibrationProgressResult(
  progress?: MotionMasterMessage.Status.CirculoEncoderNarrowAngleCalibrationProcedure.IProgress | null
): CirculoEncoderCalibrationResult | null {
  const maxPermissiblePhaseError = progress?.maxPermissiblePhaseError ?? 0;

  if (!progress || maxPermissiblePhaseError <= 0) {
    return null;
  }

  return computeCirculoEncoderCalibrationResult(
    progress.position ?? undefined,
    progress.phaseError ?? undefined,
    maxPermissiblePhaseError
  );
}
